import {CheckInOutPair} from "@src/httpClient/__generated__/Api.ts";
import {makeGroupEventsOffline} from "@src/Store/eventsStore.ts";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {
    Body_14_Coral_Empasis_14,
    Body_14_Coral_Primary_Regular_14, Body_14_Coral_Strong_emphasis_14,
    Body_16_Coral_Emphasis_16,
} from "@UI/typography/generated/typography.tsx";
import {Gt, GtDatetime} from "@src/i18n/Gt.tsx";
import {formatMsToJpTime, getCurrentDateStr, getTimeFromDT, strToTime} from "@src/utils/formatDate.ts";
import {useMemo} from "react";

import styles from "./historyDaysGroupedEventsElement.module.less";
import cn from "classnames";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {MapPinWhite} from "@UI/components/icons/MapPinWhite.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {Colors} from "@UI/theme/generated/Colors.ts";
import {MapPinOff} from "@UI/components/icons/MapPinOff.tsx";
import {Clock} from "@UI/components/icons/Clock.tsx";
import {getLocationLink, getLocationsString} from "@src/utils/coordLink.ts";
import {calculateWorkingTime} from "@src/utils/workingTimeCalculators.ts";
import {WorkDayWithEvents} from "@src/Store/employeesStore.ts";


interface LocationLinkProps {
    link?: string
    coords: string
}

const LocationLink = ({link, coords}: LocationLinkProps) => {
    if (link) return (
        <Button type={ButtonType.link} icon={<MapPinWhite size={20}/>}
                className={styles.coordsButton}
                color={ColorNames.CORAL_INK}
                externalLink={true}
                href={link}>
            <Body_14_Coral_Empasis_14>
                {coords}
            </Body_14_Coral_Empasis_14>
        </Button>
    )
    return (
        <Stack alignItems={"center"} className={styles.errorTime} gap={10}>
            <MapPinOff color={Colors.Coral_special_Error_red}/>
            <Body_14_Coral_Strong_emphasis_14>
                <Gt>No location information</Gt>
            </Body_14_Coral_Strong_emphasis_14>
        </Stack>
    )
}

export const WorkingTime = ({date, groupedEvents, total_time}: {
    date?: string,
    total_time?: string,
    groupedEvents?: CheckInOutPair[]
}) => {
    const workedHoursMS = useMemo(() => {
        if (date === getCurrentDateStr() && (total_time === "00:00:00" || !total_time)) {
            return calculateWorkingTime(groupedEvents ?? [])
        }
        return total_time ? strToTime(total_time) : 0
    }, [groupedEvents, total_time])
    //
    const workedHours = useMemo(() => {
        if (workedHoursMS === 0) return "-"
        return formatMsToJpTime(workedHoursMS)
    }, [workedHoursMS])

    return (
        <Body_14_Coral_Strong_emphasis_14>
            {workedHours}
        </Body_14_Coral_Strong_emphasis_14>
    )
}

interface Props {
    workday?: WorkDayWithEvents
    withBorder?: boolean
    showDate?: boolean
}

export const HistoryDaysGroupedEventsElement = ({workday, withBorder, showDate = true}: Props) => {
    const groupedEvents = useMemo(() => makeGroupEventsOffline(workday?.events ?? []), [workday?.events])

    const inDate = useMemo(() => {
        if (!groupedEvents || groupedEvents?.length === 0) return undefined;
        return groupedEvents[0]?.event_in?.recorded_at
    }, [groupedEvents])


    const outDate = useMemo(() => {
        if (!groupedEvents || groupedEvents?.length === 0) return undefined;
        return groupedEvents[groupedEvents.length - 1]?.event_out?.recorded_at
    }, [groupedEvents])

    const inCoords = useMemo(() => {
        if (!groupedEvents || groupedEvents?.length === 0) return "—,—";
        return groupedEvents[0].event_in ? getLocationsString(groupedEvents[0].event_in) : "—,—"
    }, [groupedEvents])

    const outCoords = useMemo(() => {
        if (!groupedEvents || groupedEvents?.length === 0) return "—,—";
        const event = groupedEvents[groupedEvents.length - 1].event_out
        return event ? getLocationsString(event) : "—,—"
    }, [groupedEvents])

    const inLink = useMemo(() => {
        if (!groupedEvents || groupedEvents?.length === 0) return undefined
        return groupedEvents[0].event_in ? getLocationLink(groupedEvents[0].event_in) : undefined
    }, [groupedEvents])

    const outLink = useMemo(() => {
        if (!groupedEvents || groupedEvents?.length === 0) return undefined
        const event = groupedEvents[groupedEvents.length - 1].event_out
        return event ? getLocationLink(event) : undefined
    }, [groupedEvents])

    return (
        <Stack direction={"column"} gap={16} className={cn(styles.extendedHistoryElem, {[styles.bordered]: withBorder})}>
            {(workday?.date && showDate) &&
                <Stack justifyContent={"space-between"}>
                    <Body_14_Coral_Strong_emphasis_14>
                        <GtDatetime>{workday?.date}</GtDatetime>
                    </Body_14_Coral_Strong_emphasis_14>
                    <WorkingTime total_time={workday?.total_time ?? undefined} date={workday?.date}
                                 groupedEvents={groupedEvents}/>
                </Stack>
            }

            <Stack alignItems={"center"} className={cn(styles.historySub)} gap={16}>
                <Stack direction={"column"} justifyContent={"center"} style={{width: "100%"}}>
                    <Body_16_Coral_Emphasis_16 style={{color: Colors.Obsidian_Dark_500}}>
                        <Gt>Start work</Gt>
                    </Body_16_Coral_Emphasis_16>
                    <Stack>
                        <Stack gap={4} className={styles.time} alignItems={"center"}>
                            <Clock size={18}/>
                            <Body_14_Coral_Primary_Regular_14>
                                {inDate
                                    ? getTimeFromDT(inDate)
                                    : <span>—:—</span>
                                }
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>

                        <Stack className={styles.coords}>
                            {inDate
                                ? <LocationLink link={inLink} coords={inCoords}/>
                                :
                                <Stack className={styles.coordsPlaceholder} alignItems={"center"} gap={4}>
                                    <MapPinWhite size={20}/>
                                    —,—
                                </Stack>
                            }
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            <Stack alignItems={"center"} className={cn(styles.historySub, styles.topBorder)} gap={16}>
                <Stack direction={"column"} justifyContent={"center"} style={{width: "100%"}}>
                    <Body_16_Coral_Emphasis_16 style={{color: Colors.Obsidian_Dark_500}}>
                        <Gt>Finish work</Gt>
                    </Body_16_Coral_Emphasis_16>
                    <Stack>
                        <Stack gap={4} className={styles.time} alignItems={"center"}>
                            <Clock size={18}/>
                            <Body_14_Coral_Primary_Regular_14>
                                {outDate
                                    ? getTimeFromDT(outDate)
                                    : <span>—:—</span>
                                }
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>

                        <Stack className={styles.coords}>
                            {outDate
                                ?
                                <LocationLink link={outLink} coords={outCoords}/>
                                :
                                <Stack className={styles.coordsPlaceholder} alignItems={"center"} gap={4}>
                                    <MapPinWhite size={20}/>
                                    —,—
                                </Stack>
                            }
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )
}
